import { useState } from 'react'
import { useSelector } from 'react-redux'
import ClassicBettingController from './ClassicBettingController'
import FreebetsBettingController from './FreebetsBettingController'
import { useFreebetsCount } from '@hooks/queries/useFreebets'
import { useBuyBonusTrigger } from '@hooks/buyBonus/useBuyBonusTrigger'
import { selectClassicBusy, selectFreebetBusy, selectRequestedFreebetId } from '@slices/bettingSlice'
import { FreebetTicketSVG } from '@media/svgs/FreebetTicketSVG'

const ACTIVE_STATUS = ['ACTIVE']

const BettingPanel = () => {
  const { freebets } = useSelector((state) => state.configurations.featureToggles)
  const classicBusy = useSelector(selectClassicBusy)
  const freebetBusy = useSelector(selectFreebetBusy)
  const requestedFreebetId = useSelector(selectRequestedFreebetId)
  const { isEnabled: buyBonusEnabled, onOpen: onOpenBuyBonus } = useBuyBonusTrigger()

  // Active freebet count for the tab badge (only queried when freebets is on).
  const { data: freebetCountData } = useFreebetsCount(
    { status: ACTIVE_STATUS },
    { enabled: Boolean(freebets) }
  )
  const freebetCount = freebetCountData?.activeCount ?? 0

  // A tab is LOCKED while a bet is committed on the OTHER session, so the player
  // can't switch sessions mid-bet. This mutual exclusion also guarantees only one
  // orchestrator ever has pending emits, which keeps place-reply routing simple.
  const BETTING_PANEL_CONTENT = {
    CLASSIC: {
      KEY: 'CLASSIC',
      TAB_NAME: 'Classic',
      COMPONENT: <ClassicBettingController />,
      IS_ENABLE: true,
      LOCKED: freebetBusy,
    },

    FREEBETS: {
      KEY: 'FREEBETS',
      TAB_NAME: 'Freebets',
      COMPONENT: <FreebetsBettingController />,
      IS_ENABLE: freebets,
      LOCKED: classicBusy,
      // count badge (shown when > 0)
      BADGE: freebetCount,
    },
  }

  const TABS = Object.values(BETTING_PANEL_CONTENT).filter(({ IS_ENABLE }) => IS_ENABLE)

  const [currentBettingTab, setCurrentBettingTab] = useState(TABS[0].KEY)

  // Bring the Freebets tab forward when the menu's Play button routes a freebet
  // into panel one. React's "adjust state on prop change" render pattern (guarded
  // by the last-seen request), so no effect / cascading render. Play is disabled
  // while classic is busy, so the FREEBETS tab is switchable here.
  const [seenRequest, setSeenRequest] = useState(requestedFreebetId)
  if (requestedFreebetId !== seenRequest) {
    setSeenRequest(requestedFreebetId)
    if (requestedFreebetId != null && freebets && !classicBusy) {
      setCurrentBettingTab('FREEBETS')
    }
  }

  const activeTab = BETTING_PANEL_CONTENT[currentBettingTab]
  const activeIndex = TABS.findIndex((tab) => tab.KEY === currentBettingTab)

  const handleTabChange = (tab) => {
    if (!tab || tab.LOCKED) return
    setCurrentBettingTab(tab.KEY)
  }

  return (
    <div className="standard-normal-betting-panel-outer-container">
      <div className="standard-normal-betting-panel-inner-container">
        <div className="standard-normal-betting-panel-header-container">
          <div
            className="standard-normal-betting-panel-header-tab-container"
            style={{ '--betting-tab-count': TABS.length, '--betting-tab-index': activeIndex }}
          >
            {/* Sliding pill that tracks the active tab. */}
            <span className="standard-normal-betting-panel-tab-slider" aria-hidden="true" />
            {TABS.map((tab) => (
              <div
                key={tab.KEY}
                className={`standard-normal-betting-panel-tab ${tab.KEY === currentBettingTab ? 'standard-normal-betting-panel-active-tab' : ''} ${tab.LOCKED ? 'disable-actions' : ''} `}
                onClick={() => handleTabChange(tab)}
              >
                {tab.TAB_NAME}
                {tab.BADGE > 0 && (
                  <span className="standard-normal-betting-panel-tab-badge">{tab.BADGE}</span>
                )}
              </div>
            ))}
          </div>
          <div className="standard-normal-betting-panel-header-extra-container">
            {buyBonusEnabled && (
              <button
                className="standard-normal-betting-panel-buy-bonus-button"
                onClick={onOpenBuyBonus}
              >
                <span className="standard-normal-betting-panel-buy-bonus-button-icon">
                  <FreebetTicketSVG width="14px" height="11px" />
                </span>
                <span className="standard-normal-betting-panel-buy-bonus-button-text">Buy Bonus</span>
              </button>
            )}
          </div>
        </div>
        <div className="standard-normal-betting-panel-body-container">{activeTab.COMPONENT}</div>
      </div>
    </div>
  )
}

export default BettingPanel
